import { useEffect, useState, useRef } from "react";
import {
  Box,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  Link,
  CircularProgress,
  Button,
  Tooltip,
  Autocomplete,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch, useSelector } from "react-redux";
import API_MIDDLEWARE from "../../redux/api/middleware";
import { APP_CUSTOM_ERROR } from "../../constants/AppVarConstants";
import { setErrorMessage, setSuccessMessage } from "../../redux/actions/appActions";
import { JOBS_LISTING_URL, UPDATE_JOB_STATUS_URL } from "../../redux/api/config";
import InfiniteScroll from "react-infinite-scroll-component";
import moment from "moment";
import { Edit } from "@mui/icons-material";
import { debounce } from "lodash";
import { ROUTE_CLIENT_JOB_DETAILS, ROUTE_COMPANY_CREATE_JOB, ROUTE_COMPANY_JOB_DETAILS, ROUTE_CREATE_JOB } from "../../constants/routes";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getCompanyList } from "../../redux/actions/companyList";
import JobCard from "./JobCard";

const SELECTED_COMPANY_STORAGE_KEY = "selectedCompanyDashboard";

const CompanyJobsDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

    const user = useSelector((state: any) => state.app.user);
    const { companyName,companyId } = user?.user || {};
  const roles = localStorage.getItem("roles");
  const isJobUser = roles?.includes('EMPLOYER_USER');
  const companyList = useSelector((state: any) => state.companyList.list);
  const [hasMore, setHasMore] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [companySearchText, setCompanySearchText] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [allCompanies, setAllCompanies] = useState<any[]>([]);
  const [hasMoreCompanies, setHasMoreCompanies] = useState(true);
  const [companyPage, setCompanyPage] = useState(1);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [showActiveJobs, setShowActiveJobs] = useState(true);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [jobToUpdate, setJobToUpdate] = useState<any>(null);
  const [newStatus, setNewStatus] = useState<string>("ACTIVE");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [newJobCount, setNewJobCount] = useState(0);

  const fetchJobs = (flag: boolean, resetOffset: boolean, jobAction = false) => {
    // Prevent multiple simultaneous API calls
    if (loading && !flag) {
      return;
    }

    if (!flag) {
      setLoading(true);
    }

    const payload: any = {
      search_query: searchText,
      page: jobAction ? currentPage : resetOffset ? 1 : currentPage + 1,
      fetchSize: 12,
      status: showActiveJobs ? 1 : 2,
    };

    // Add company_id only if a company is explicitly selected from dropdown
    if (selectedCompany?.companyId) {
      payload.company_id = selectedCompany.companyId;
    }

    API_MIDDLEWARE(JOBS_LISTING_URL, "POST", { "Content-Type": "application/json" }, payload)
      .then((res: any) => {
        const { records, hasNext, page, totalRecords, newJobCount } = res?.result || {};
        const jobs: any = resetOffset ? [...records] : [...data, ...records];
        setData(jobs);
        setCurrentPage(page);
        setHasMore(hasNext);
        setLoading(false);
        setTotal(totalRecords);
        setNewJobCount(newJobCount || 0);
      })
      .catch((err: any) => {
        // Only show error message if it's not an authentication error
        if (err.code !== 401) {
          dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        }
        setLoading(false);
      });
  };

  const debouncedFetchJobs = debounce(() => fetchJobs(false, true), 500);

  const fetchCompanies = async (searchQuery: string, page = 1, reset = false) => {
    if (loadingCompanies) return;

    setLoadingCompanies(true);
    try {
      // Dispatch the action
      dispatch(getCompanyList({ searchText: searchQuery, fetchSize: 10, page: page, isPagination: false }));

      // For pagination, we need to accumulate results
      if (!reset && page > 1) {
        // This is a pagination request, we'll handle it in the useEffect
      }
    } catch (err: any) {
      console.error("Error fetching companies:", err);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const debouncedFetchCompanies = debounce((searchQuery: string) => {
    setScrollPosition(0); // Reset scroll position for new search
    // fetchCompanies(searchQuery, 1, true);
  }, 500);

  const handleStatusChangeClick = (jobId: string, newStatusValue: string) => {
    // JobCard already calculates the new status, so use it directly
    setNewStatus(newStatusValue);
    setJobToUpdate({ jobId, newStatus: newStatusValue });
    setStatusDialogOpen(true);
  };

  const handleStatusConfirm = async () => {
    if (!jobToUpdate?.jobId) return;

    setIsUpdatingStatus(true);
    try {
      // Prepare payload - include postedDate when activating
      const payload: any = {};
      if (newStatus === "ACTIVE") {
        // Set current date as postedDate when activating
        payload.postedDate = new Date().toISOString();
      }

      const res = await API_MIDDLEWARE(
        UPDATE_JOB_STATUS_URL(jobToUpdate.jobId, newStatus),
        "PUT",
        { "Content-Type": "application/json" },
        payload
      );

      dispatch(setSuccessMessage(`Job ${newStatus === "ACTIVE" ? 'activated' : 'deactivated'} successfully!`));

      // Remove job from current view if it no longer matches the filter
      // If viewing Active jobs and deactivating, or viewing Inactive jobs and activating, remove it
      const shouldRemove = (showActiveJobs && newStatus !== "ACTIVE") || (!showActiveJobs && newStatus === "ACTIVE");
      
      if (shouldRemove) {
        setData((prevData: any[]) =>
          prevData.filter((job: any) => job.jobId !== jobToUpdate.jobId)
        );
        setTotal((prevTotal) => Math.max((prevTotal || 0) - 1, 0));
      } else {
        // Update the job in place if it still matches the filter
        setData((prevData: any[]) =>
          prevData.map((job: any) =>
            job.jobId === jobToUpdate.jobId
              ? { 
                  ...job, 
                  status: newStatus,
                  // Update postedDate if activating
                  ...(newStatus === "ACTIVE" && { postedDate: payload.postedDate })
                }
              : job
          )
        );
      }

      // Close dialog
      setStatusDialogOpen(false);
      setJobToUpdate(null);

      return res?.result;
    } catch (err: any) {
      dispatch(setErrorMessage(err?.errorMsg ?? APP_CUSTOM_ERROR));
      return false;
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleStatusCancel = () => {
    setStatusDialogOpen(false);
    setJobToUpdate(null);
    setIsUpdatingStatus(false);
  };
  const handleEditClick = (jobId: string, companyId: string) => {
    // Store selected company before navigating
    if (selectedCompany) {
      localStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, JSON.stringify(selectedCompany));
    }
    navigate(`${ROUTE_COMPANY_CREATE_JOB.replace(':companyId', companyId)}?edit=true&jobId=${jobId}`);
  };
  const handleViewClick = (jobId: string) => {
    // Store selected company before navigating
    if (selectedCompany) {
      localStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, JSON.stringify(selectedCompany));
    }
    navigate(`${ROUTE_COMPANY_JOB_DETAILS}/${jobId}`);
  };
  // Restore selected company from localStorage or URL params on mount
  useEffect(() => {
    // First, fetch companies so we can match the stored company
    setScrollPosition(0); // Reset scroll position on initial load
    fetchCompanies("", 1, true);
    
    // Try to restore from URL query params first (if coming back from edit page)
    const companyIdFromUrl = searchParams.get('companyId');
    
    // Then try to restore from localStorage
    const storedCompany = localStorage.getItem(SELECTED_COMPANY_STORAGE_KEY);
    
    if (companyIdFromUrl || storedCompany) {
      // We'll restore the company after companies are loaded
      // This will be handled in the useEffect that watches companyList
    }
  }, []);

  // Track if company restoration is complete
  const [companyRestored, setCompanyRestored] = useState(false);
  const [isRestoringCompany, setIsRestoringCompany] = useState(false);
  const [userManuallyCleared, setUserManuallyCleared] = useState(false);

  // Restore selected company once companies are loaded
  useEffect(() => {
    if (companyList.length > 0 && !selectedCompany && !isRestoringCompany && !userManuallyCleared) {
      setIsRestoringCompany(true);
      
      // Try to restore from URL query params first
      const companyIdFromUrl = searchParams.get('companyId');
      
      if (companyIdFromUrl) {
        const companyFromUrl = companyList.find((c: any) => c.companyId === parseInt(companyIdFromUrl));
        if (companyFromUrl) {
          setSelectedCompany(companyFromUrl);
          setCompanyRestored(true);
          setIsRestoringCompany(false);
          // Clear the URL param after restoring
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.delete('companyId');
          navigate({ search: newSearchParams.toString() }, { replace: true });
          return;
        }
      }
      
      // Try to restore from localStorage
      const storedCompany = localStorage.getItem(SELECTED_COMPANY_STORAGE_KEY);
      if (storedCompany) {
        try {
          const parsedCompany = JSON.parse(storedCompany);
          // Verify the company still exists in the list
          const foundCompany = companyList.find((c: any) => c.companyId === parsedCompany.companyId);
          if (foundCompany) {
            setSelectedCompany(foundCompany);
            setCompanyRestored(true);
            setIsRestoringCompany(false);
          } else {
            // Company not found, clear storage
            localStorage.removeItem(SELECTED_COMPANY_STORAGE_KEY);
            setCompanyRestored(true); // Mark as restored even if not found
            setIsRestoringCompany(false);
          }
        } catch (error) {
          console.error('Error parsing stored company:', error);
          localStorage.removeItem(SELECTED_COMPANY_STORAGE_KEY);
          setCompanyRestored(true); // Mark as restored even on error
          setIsRestoringCompany(false);
        }
      } else {
        // No stored company, mark as restored
        setCompanyRestored(true);
        setIsRestoringCompany(false);
      }
    } else if (companyList.length > 0 && selectedCompany && !companyRestored) {
      // Company already selected, mark as restored
      setCompanyRestored(true);
      setIsRestoringCompany(false);
    }
  }, [companyList, searchParams, selectedCompany, navigate, isRestoringCompany, companyRestored]);

  // Track if initial jobs have been fetched
  const [initialJobsFetched, setInitialJobsFetched] = useState(false);
  const prevSelectedCompanyRef = useRef<any>(null);

  // Initial load effect - fetch jobs after companies are loaded AND company restoration is complete
  // This ensures selectedCompany is set before fetching jobs
  useEffect(() => {
    if (companyList.length > 0 && companyRestored && !isRestoringCompany) {
      // If this is the first fetch, or if selectedCompany was just restored (changed from null to a value)
      const wasRestored = prevSelectedCompanyRef.current === null && selectedCompany !== null;
      
      if (!initialJobsFetched || wasRestored) {
        // Wait a tick to ensure selectedCompany state is updated
        // Then fetch jobs - selectedCompany will be used in the payload if it exists
        const timer = setTimeout(() => {
          fetchJobs(false, true);
          setInitialJobsFetched(true);
          prevSelectedCompanyRef.current = selectedCompany;
        }, 0);
        return () => clearTimeout(timer);
      }
    }
  }, [companyList.length, companyRestored, isRestoringCompany, initialJobsFetched, selectedCompany]);

  // Handle search text changes with debouncing
  useEffect(() => {
    if (hasInitiallyLoaded) {
      // Reset existing data when search text changes
      setData([]);
      setCurrentPage(1);
      setHasMore(false);
      setTotal(0);
      
      debouncedFetchJobs();
    }

    return debouncedFetchJobs.cancel;
  }, [searchText]);

  // Handle active/inactive filter changes
  useEffect(() => {
    if (hasInitiallyLoaded) {
      // Reset existing data when status filter changes
      setData([]);
      setCurrentPage(1);
      setHasMore(false);
      setTotal(0);
      
      fetchJobs(false, true);
    }
  }, [showActiveJobs]);

  // Handle company selection changes
  useEffect(() => {
    // Store selected company in localStorage whenever it changes
    if (selectedCompany) {
      localStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, JSON.stringify(selectedCompany));
    } else {
      // Clear storage if company is deselected
      localStorage.removeItem(SELECTED_COMPANY_STORAGE_KEY);
    }
    
    // Only refetch jobs if company restoration is complete and this is a user-initiated change
    if (companyRestored && hasInitiallyLoaded) {
      // Reset existing data when company changes
      setData([]);
      setCurrentPage(1);
      setHasMore(false);
      setTotal(0);
      
      // Only fetch if we have a valid company selected or if we're clearing the selection
      if (selectedCompany?.companyId || selectedCompany === null) {
        fetchJobs(false, true);
      }
    }
  }, [selectedCompany, companyRestored, hasInitiallyLoaded]);

  // Mark initial load as complete after first render
  useEffect(() => {
    setHasInitiallyLoaded(true);
  }, []);

  useEffect(() => {
    debouncedFetchCompanies(companySearchText);
    return debouncedFetchCompanies.cancel;
  }, [companySearchText]);

  // Sync Redux company list with local state and handle pagination
  useEffect(() => {
    if (companyList.length > 0) {
      if (companyPage === 1) {
        // First page or search reset
        setAllCompanies(companyList);
      } else {
        // Pagination - append to existing list
        setAllCompanies(prev => {
          const newCompanies = companyList.filter((newCompany: any) =>
            !prev.some((existingCompany: any) => existingCompany.companyId === newCompany.companyId)
          );
          return [...prev, ...newCompanies];
        });
      }
    }
  }, [companyList, companyPage]);

  return (
    <Box
      sx={{
        backgroundColor: "#e5e4fe",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >

      <Box
        sx={{
          backgroundColor: "#e5e4fe",
          width: "100%",
          padding: "20px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Left side content: Company dropdown */}
        <Box
          sx={{
            marginLeft: "32px",
            display: "flex",
            alignItems: "center",
            width: "320px",
            height: "45px",
            border: "1px solid #756DE9",
            borderRadius: "100px",
            position: "relative",
          }}
        >
          <Autocomplete
            fullWidth
            options={allCompanies}
            value={selectedCompany}
            onChange={(event, newValue) => {
              // Handle clearing - newValue can be null or a string (from freeSolo) or the company object
              if (newValue === null || newValue === "") {
                setSelectedCompany(null);
                // Clear from localStorage when user manually clears
                localStorage.removeItem(SELECTED_COMPANY_STORAGE_KEY);
                // Mark that user manually cleared to prevent restoration
                setUserManuallyCleared(true);
              } else if (typeof newValue === "string") {
                // If it's a string from freeSolo, try to find matching company
                const foundCompany = allCompanies.find(
                  (company: any) => company.company_name === newValue
                );
                setSelectedCompany(foundCompany || null);
                // Reset manual clear flag if user selects a company
                if (foundCompany) {
                  setUserManuallyCleared(false);
                }
              } else {
                // It's a company object
                setSelectedCompany(newValue);
                // Reset manual clear flag if user selects a company
                setUserManuallyCleared(false);
              }
            }}
            onInputChange={(event, newInputValue) => {
              setCompanySearchText(newInputValue);
              // Clear selected company when input is cleared
              if (newInputValue === "") {
                setSelectedCompany(null);
                // Also clear from localStorage when user manually clears
                localStorage.removeItem(SELECTED_COMPANY_STORAGE_KEY);
                // Mark that user manually cleared to prevent restoration
                setUserManuallyCleared(true);
              }
            }}
            getOptionLabel={(option) => option?.company_name || ""}
            isOptionEqualToValue={(option, value) => option?.companyId === value?.companyId}
            loading={loadingCompanies}
            loadingText="Loading companies..."
            noOptionsText="No companies found"
            freeSolo
            clearOnBlur={false}
            selectOnFocus={false}
            disableClearable={false}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "95px",
                boxShadow: "0px 0px 11px 2px #4B44D41A inset",
                fontSize: "16px",
                fontWeight: "500",
                color: "#000",
                "& fieldset": {
                  borderColor: "#E0E0E0",
                },
                "&:hover fieldset": {
                  borderColor: "#756DE9",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#756DE9",
                },
              },
              "& .MuiInputBase-input": {
                padding: "12px 14px",
                paddingRight: "60px !important", // Increased to make room for clear button
              },
              "& .MuiAutocomplete-endAdornment": {
                right: "12px",
                "& .MuiAutocomplete-clearIndicator": {
                  display: "flex",
                  visibility: "visible",
                  opacity: 1,
                  padding: "4px",
                  "&:hover": {
                    opacity: 0.7,
                  },
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search and select company..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCompanies ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  fontSize: "15px",
                  fontWeight: "500",
                  padding: "12px 16px",
                  margin: "2px 8px",
                  borderRadius: "8px",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#F0F0FF",
                    color: "#756DE9",
                    transform: "translateX(4px)",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#F0F0FF",
                    color: "#756DE9",
                  },
                }}
              >
                {option.company_name || "Unknown Company"}
              </Box>
            )}
            // ListboxProps={{
            //   onScroll: (event: React.SyntheticEvent) => {
            //     const listboxNode = event.currentTarget;
            //     if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 5) {
            //       if (hasMoreCompanies && !loadingCompanies) {


            //         // Update companyPage before fetching next page
            //         setCompanyPage(prevPage => {
            //           const nextPage = prevPage + 1;
            //           fetchCompanies(companySearchText, nextPage, false);
            //           return nextPage;
            //         });
            //       }
            //     }
            //   },
            // }}
            ListboxComponent={({ children, ...other }) => {
              const listboxRef = useRef<HTMLUListElement>(null);

              // Restore scroll position after new items are loaded
              useEffect(() => {
                if (listboxRef.current && scrollPosition > 0) {
                  listboxRef.current.scrollTop = scrollPosition;
                }
              }, [allCompanies.length, scrollPosition]);

              return (
                <Box
                  ref={listboxRef}
                  component="ul"
                  {...other}
                  sx={{
                    maxHeight: 300,
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                      width: "6px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "#F5F5F5",
                      borderRadius: "3px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "#C1C1C1",
                      borderRadius: "3px",
                      "&:hover": {
                        background: "#A8A8A8",
                      },
                    },
                  }}
                >
                  {children}
                  {loadingCompanies && hasMoreCompanies && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "12px 16px",
                        fontSize: "14px",
                        color: "#999999",
                      }}
                    >
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Loading more companies...
                    </Box>
                  )}
                </Box>
              );
            }}
            PaperComponent={({ children, ...other }) => (
              <Box
                {...other}
                sx={{
                  borderRadius: "16px",
                  boxShadow: "0px 12px 40px rgba(0, 0, 0, 0.15), 0px 4px 16px rgba(0, 0, 0, 0.08)",
                  border: "1px solid #E8E8F0",
                  marginTop: "8px",
                  backgroundColor: "#FFFFFF",
                }}
              >
                {children}
              </Box>
            )}
          />
        </Box>

        {/* Center: Active/Inactive Toggle Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              backgroundColor: "#F5F5F5",
              borderRadius: "25px",
              padding: "4px",
              border: "1px solid #E0E0E0",
            }}
          >
            <Button
              onClick={() => setShowActiveJobs(true)}
              sx={{
                borderRadius: "20px",
                padding: "8px 24px",
                fontSize: "14px",
                fontWeight: "500",
                textTransform: "none",
                backgroundColor: showActiveJobs ? "#756DE9" : "transparent",
                color: showActiveJobs ? "#FFFFFF" : "#666666",
                minWidth: "100px",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: showActiveJobs ? "#5a4dbf" : "#E8E8F0",
                  color: showActiveJobs ? "#FFFFFF" : "#333333",
                },
                boxShadow: showActiveJobs ? "0px 2px 8px rgba(117, 109, 233, 0.3)" : "none",
              }}
            >
              Active
            </Button>
            <Button
              onClick={() => setShowActiveJobs(false)}
              sx={{
                borderRadius: "20px",
                padding: "8px 24px",
                fontSize: "14px",
                fontWeight: "500",
                textTransform: "none",
                backgroundColor: !showActiveJobs ? "#756DE9" : "transparent",
                color: !showActiveJobs ? "#FFFFFF" : "#666666",
                minWidth: "100px",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: !showActiveJobs ? "#5a4dbf" : "#E8E8F0",
                  color: !showActiveJobs ? "#FFFFFF" : "#333333",
                },
                boxShadow: !showActiveJobs ? "0px 2px 8px rgba(117, 109, 233, 0.3)" : "none",
              }}
            >
              Inactive
            </Button>
          </Box>
        </Box>

        {/* Right side: Search button */}
        <Box
          sx={{
            marginRight: "32px",
            display: "flex",
            alignItems: "center",
            width: "359px",
            height: "45px",
            border: "1px solid #756DE9",
            borderRadius: "100px",
            position: "relative",
          }}
        >
          {/* Search input */}
          <TextField
            variant="standard"
            placeholder="Search..."
            sx={{
              width: "100%",
              paddingLeft: "15px",
              fontSize: "16px",
              "& .MuiInput-underline:before": { borderBottom: "none" },
              "& .MuiInput-underline:after": { borderBottom: "none" },
            }}
            InputProps={{
              disableUnderline: true,
            }}
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText}
          />

          {/* Search Icon */}
          <IconButton
            sx={{
              position: "absolute",
              right: "10px",
              padding: "0",
            }}
          >
            <Box
              component="img"
              src="/images/client-website/search-line.png"
              alt="search icon"
              sx={{
                width: "24px",
                height: "24px",
              }}
            />
          </IconButton>

          {/* Dropdown for recent searches */}
          {/* <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{
              style: {
                width: "315px",
                backgroundColor: "#fff",
                padding: "20px", // Custom padding
                borderRadius: "10px", // Rounded corners
                border: "1px solid #756DE9",
                marginTop: "10px",
              },
            }}
          >
            {searches.map((search, index) => (
              <MenuItem
                key={index}
                onClick={() => handleSelect(search)} // When an option is clicked, the value will be selected
                sx={{
                  fontSize: "16px",
                  fontWeight: "400",
                  color: "#444242",
                }}
              >
                {search}
              </MenuItem>
            ))}
          </Menu> */}
        </Box>
      </Box>
      <Box
        sx={{
          padding: { xs: "20px", sm: 4 },
          backgroundColor: "#fff",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          width: "100%",
        }}
      >
        <Box
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 4, marginBottom: "24px" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: "600" }}>
              {showActiveJobs ? "Active" : "Inactive"} Job Posts ({total})
            </Typography>
            {newJobCount > 0 && !isJobUser && (
              <Chip
                label={`${newJobCount} New`}
                sx={{
                  backgroundColor: "#4CAF50",
                  color: "#fff",
                  fontWeight: "600",
                  fontSize: "12px",
                  height: "28px",
                  borderRadius: "14px",
                  "& .MuiChip-label": {
                    px: 1.5,
                  },
                }}
              />
            )}
          </Box>

          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            disabled={!selectedCompany}
            sx={{
              backgroundColor: selectedCompany ? "#756DE9" : "#E0E0E0",
              color: selectedCompany ? "#fff" : "#999999",
              borderRadius: "100px",
              padding: "10px 20px",
              "&:hover": {
                backgroundColor: selectedCompany ? "#5a4dbf" : "#E0E0E0",
                color: selectedCompany ? "#fff" : "#999999",
                borderColor: selectedCompany ? "#5a4dbf" : "#E0E0E0",
              },
              "&:disabled": {
                backgroundColor: "#E0E0E0",
                color: "#999999",
                borderColor: "#E0E0E0",
              },
              textTransform: "capitalize",
            }}
            onClick={() => {
              // Store selected company before navigating
              if (selectedCompany) {
                localStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, JSON.stringify(selectedCompany));
              }
              navigate(ROUTE_COMPANY_CREATE_JOB.replace(':companyId', selectedCompany.companyId));
            }}

          >
            Create New Job Post
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : data.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "60px 20px",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                backgroundColor: "#F5F5F5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "24px",
              }}
            >
              <Box
                component="img"
                src="/images/client-website/search-line.png"
                alt="No data"
                sx={{
                  width: "40px",
                  height: "40px",
                  opacity: 0.5,
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: "600",
                color: "#333333",
                marginBottom: "8px",
              }}
            >
              No {showActiveJobs ? "Active" : "Inactive"} Jobs Found
            </Typography>
            <Typography
              sx={{
                fontSize: "16px",
                color: "#666666",
                marginBottom: "24px",
                maxWidth: "400px",
              }}
            >
              {showActiveJobs
                ? "There are currently no active job posts. Create a new job post to get started."
                : "There are currently no inactive job posts. Switch to active jobs to see available positions."
              }
            </Typography>
            {showActiveJobs && (
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                disabled={!selectedCompany}
                sx={{
                  backgroundColor: selectedCompany ? "#756DE9" : "#E0E0E0",
                  color: selectedCompany ? "#fff" : "#999999",
                  borderRadius: "100px",
                  padding: "12px 24px",
                  "&:hover": {
                    backgroundColor: selectedCompany ? "#5a4dbf" : "#E0E0E0",
                  },
                  "&:disabled": {
                    backgroundColor: "#E0E0E0",
                    color: "#999999",
                  },
                  textTransform: "capitalize",
                }}
                onClick={() => {
                  // Store selected company before navigating
                  if (selectedCompany) {
                    localStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, JSON.stringify(selectedCompany));
                  }
                  navigate(`${ROUTE_COMPANY_CREATE_JOB.replace(':companyId', selectedCompany.companyId)}`);
                }}
              >
                Create New Job Post
              </Button>
            )}
          </Box>
        ) : (
          <InfiniteScroll
            dataLength={data.length}
            next={() => fetchJobs(true, false)}
            hasMore={hasMore}
            loader={
              <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
                <CircularProgress
                  size={40}
                  sx={{
                    color: "#756DE9",
                    "& .MuiCircularProgress-circle": {
                      strokeLinecap: "round",
                    }
                  }}
                />
              </Box>
            }
            endMessage={
              <Box component="p" sx={{ textAlign: "center", mt: 2 }}>
                <b></b>
              </Box>
            }
          >
            <Grid container spacing={2} px={4} justifyContent="flex-start">
              {data.map((el: any, idx: number) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <JobCard {...el} onStatusChange={handleStatusChangeClick} companyName={el?.company?.companyName || "--"} onEditClick={handleEditClick}
                   onViewClick={handleViewClick}/>
                </Grid>
              ))}
            </Grid>
          </InfiniteScroll>
        )}
      </Box>

      {/* Status Change Confirmation Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={handleStatusCancel}
        aria-labelledby="status-dialog-title"
        aria-describedby="status-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 1,
          }
        }}
      >
        <DialogTitle
          id="status-dialog-title"
          sx={{
            color: newStatus === "ACTIVE" ? '#4CAF50' : '#f44336',
            fontWeight: 600,
            fontSize: '1.25rem',
            textAlign: 'center',
          }}
        >
          Confirm {newStatus === "ACTIVE" ? 'Activation' : 'Deactivation'}
        </DialogTitle>
        <DialogContent>
          <Typography
            id="status-dialog-description"
            sx={{
              textAlign: 'center',
              color: '#666',
              fontSize: '1rem',
              padding: '8px 0',
            }}
          >
            Are you sure you want to {newStatus === "ACTIVE" ? 'activate' : 'deactivate'} this job post?
            <br />
            {newStatus === "ACTIVE"
              ? 'This will make the job visible to candidates.'
              : 'This will hide the job from candidates.'
            }
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, padding: '16px 24px' }}>
          <Button
            onClick={handleStatusCancel}
            variant="outlined"
            disabled={isUpdatingStatus}
            sx={{
              borderColor: '#756DE9',
              color: '#756DE9',
              '&:hover': {
                borderColor: '#5a52d5',
                backgroundColor: 'rgba(117, 109, 233, 0.08)',
              },
              borderRadius: 2,
              paddingX: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleStatusConfirm}
            variant="contained"
            disabled={isUpdatingStatus}
            sx={{
              backgroundColor: newStatus === "ACTIVE" ? '#4CAF50' : '#f44336',
              '&:hover': {
                backgroundColor: newStatus === "ACTIVE" ? '#45a049' : '#d32f2f',
              },
              borderRadius: 2,
              paddingX: 3,
              minWidth: 100,
            }}
          >
            {isUpdatingStatus ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} sx={{ color: 'white' }} />
                {newStatus === "ACTIVE" ? 'Activating...' : 'Deactivating...'}
              </Box>
            ) : (
              newStatus === "ACTIVE" ? 'Activate' : 'Deactivate'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyJobsDashboard;
